import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { message, Table } from 'antd'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import useAxios, { axiosInstance } from '../../../hooks/useAxios'
import { EAction, IProduct, IProductTransaction } from '../../../types/product'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'
import dayjs, { Dayjs } from 'dayjs'
import {
  ONLY_DATE_FORMAT,
  ONLY_DATE_TIME_FORMAT,
  ONLY_TIME_FORMAT,
} from '../../../constants/global'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { PaginationResponseInterface } from '../../../types/pagination'
import { useCallback, useState } from 'react'
import Pagination from '../../../components/Pagination'
import PageUrls from '../../../constants/page-urls'
import { useNavigate } from 'react-router-dom'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'

interface ProductStockFormModalProps {
  isOpen: boolean
  onClose: (visible: boolean) => void
  product: IProduct
  onUpdateProductSuccess: () => void
}

interface IProductForm {
  action?: EAction | string
  quantity: number
  description?: string
  date?: Dayjs
}

const getProductFormValidationSchema = () => {
  return Yup.object({
    action: Yup.string().required('กรุณาเลือกประเภท'),
    quantity: Yup.number()
      .required('กรุณากรอกจำนวนสินค้า')
      .min(1, 'ต้องมีอย่างน้อย 1 ชิ้น'),
    description: Yup.string(),
    date: Yup.string().nullable().notRequired(),
  })
}

const menuItemsActionStatus = [
  {
    label: 'เพิ่มสินค้า',
    value: EAction.ADD_QUANTITY,
  },
  {
    label: 'ลดสินค้า',
    value: EAction.REDUCE_QUANTITY,
  },
]

export const ProductStockFormModal = (props: ProductStockFormModalProps) => {
  const { isOpen, onClose, product, onUpdateProductSuccess } = props
  const navigate = useNavigate()

  const [filter, setFilter] = useState<any>({
    page: 1,
    perPage: 15,
    search: '',
    startDate: undefined,
    endDate: undefined,
  })

  const formik = useFormik({
    initialValues: {
      action: '',
      quantity: 0,
      description: '',
      date: dayjs(),
    },
    validationSchema: getProductFormValidationSchema(),
    onSubmit: async (values: IProductForm) => {
      if (!values.date) {
        message.error('กรุณาเลือกวันที่')
        return
      }
      try {
        await axiosInstance.put(`/product/stock/${product.objectId}`, {
          ...values,
        })
        message.success('บันทึกข้อมูลสำเร็จ')
        formik.resetForm()
        onClose(false)
        onUpdateStockSuccess()
        onUpdateProductSuccess()
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
    },
  })

  const columns = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center' as const,
      width: 80,
      render: (numberRow: number) => {
        return (
          <Box>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign={'center'}
            >
              {numberRow}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'วันที่ลงข้อมูล',
      dataIndex: 'date',
      key: 'date',
      width: 160,
      align: 'center' as const,
      render: (date: string) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {dayjs(date || undefined).format(ONLY_DATE_FORMAT)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(date || undefined).format(ONLY_TIME_FORMAT)}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'คำสั่งซื้อ',
      dataIndex: 'orderCode',
      key: 'orderCode',
      align: 'center' as const,
      render: (orderCode: number) => {
        return (
          <Box sx={{ minWidth: 60 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              onClick={
                orderCode
                  ? () => {
                      navigate(`${PageUrls.Orders.Detail}/${orderCode}`, {
                        replace: true,
                      })
                    }
                  : undefined
              }
              sx={{ cursor: orderCode ? 'pointer' : 'default' }}
            >
              {orderCode || <em>ไม่ระบุ</em>}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'ก่อน',
      dataIndex: 'quantityBefore',
      key: 'quantityBefore',
      align: 'center' as const,
      render: (quantityBefore: number) => {
        return (
          <Box sx={{ minWidth: 60 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {quantityBefore?.toLocaleString() ?? 0}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'จำนวน',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center' as const,
      render: (quantity: number, productTransaction: IProductTransaction) => {
        return (
          <Box sx={{ minWidth: 60 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {quantity?.toLocaleString() ?? 0}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'หลัง',
      dataIndex: 'quantityAfter',
      key: 'quantityAfter',
      align: 'center' as const,
      render: (quantityAfter: number) => {
        return (
          <Box sx={{ minWidth: 60 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {quantityAfter?.toLocaleString() ?? 0}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'หมายเหตุ',
      dataIndex: 'description',
      key: 'description',
      align: 'center' as const,
      render: (
        description: string,
        productTransaction: IProductTransaction,
      ) => {
        return (
          <Box sx={{ minWidth: 120 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {description?.length > 0 ? description : <em>ไม่ระบุ</em>}: โดย{' '}
              {productTransaction.actor}
            </Typography>
          </Box>
        )
      },
    },
  ]

  const onFilterChange = (filter: any) => {
    setFilter(filter)
  }

  const productTransactionList = useAxios<
    PaginationResponseInterface<IProductTransaction>
  >({
    method: 'GET',
    url: `/product/transactions/${product?._id}`,
    params: {
      ...filter,
    },
  })

  const onUpdateStockSuccess = useCallback(async () => {
    try {
      await productTransactionList.mutate()
    } catch (error) {}
  }, [])

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="xl">
      <DialogTitle>{product ? 'แก้ไขสินค้า' : 'เพิ่มสินค้าใหม่'}</DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={4}>
              <FormControl variant="filled" fullWidth>
                <InputLabel id="action-label">ประเภท</InputLabel>
                <Select
                  id="action"
                  name="action"
                  labelId="action-label"
                  value={formik.values.action}
                  onChange={(e) =>
                    formik.setFieldValue('action', e.target.value)
                  }
                  onBlur={formik.handleBlur}
                  fullWidth
                >
                  {menuItemsActionStatus.map((item, index: number) => (
                    <MenuItem value={item.value} key={index}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                id="quantity"
                fullWidth
                label="จำนวน"
                name="quantity"
                helperText={
                  formik.touched.quantity && (formik.errors.quantity as string)
                }
                error={!!(formik.touched.quantity && formik.errors.quantity)}
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  const quantity = Math.max(0, Number(e.target.value) || 0)
                  formik.setFieldValue('quantity', quantity)
                }}
                value={formik.values.quantity}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  sx={{ width: '100%' }}
                  ampm={false}
                  format={ONLY_DATE_TIME_FORMAT}
                  // disableFuture
                  label="วันที่ลงข้อมูล"
                  onChange={(newValue) =>
                    formik.setFieldValue('date', dayjs(newValue))
                  }
                  value={formik.values.date ? dayjs(formik.values.date) : null}
                  slotProps={{
                    textField: {
                      helperText: '',
                      error: false,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="description"
                multiline
                fullWidth
                rows={4}
                label="กรอกรายละเอียดเพิ่มเติม"
                name="description"
                helperText={
                  formik.touched.description &&
                  (formik.errors.description as string)
                }
                error={
                  !!(formik.touched.description && formik.errors.description)
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.description}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <Table
                size={'middle'}
                columns={columns}
                scroll={{
                  x: columns.reduce(
                    (pre, cur: any) => (cur.width ? cur.width + pre : pre),
                    0,
                  ),
                }}
                dataSource={productTransactionList?.data?.records.map(
                  (productTransaction: IProductTransaction, index: number) => {
                    return {
                      ...productTransaction,
                      key: index,
                      numberRow:
                        index +
                        1 +
                        ((filter.page || 0) - 1) * (filter.perPage || 0),
                    }
                  },
                )}
                bordered
                pagination={false}
              />
              <Pagination
                total={productTransactionList?.data?.count || 0}
                current={filter.page!}
                pageSize={filter.perPage!}
                onChange={(page, perPage) =>
                  onFilterChange({ ...filter, page, perPage })
                }
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                startIcon={<SaveIcon />}
                color="primary"
                type="submit"
                size="large"
                variant="contained"
              >
                บันทึกข้อมูล
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                size="large"
                variant="contained"
                onClick={() => onClose(false)}
                startIcon={<ClearIcon />}
              >
                ปิดหน้าต่าง
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
